import autocomplete from "autocompleter";
import { Formio } from "formiojs";
import { AddressProvider } from "formiojs/providers/address/AddressProvider";
import _ from "lodash";

const GOOGLE_MAPS_LIBRARY = "googleMaps";
const GOOGLE_MAPS_BASE_URL = "https://maps.googleapis.com/maps/api/js";
const GOOGLE_MAPS_API_KEY = "AIzaSyBXH7FhFIS2Nuut_W6ww3mRvEJIAGF0_x0";
const GOOGLE_ADDRESS_STYLE_ID = "formio-google-address-provider-styles";
const GOOGLE_ADDRESS_CLASS = "google-address-suggestions";
const MIN_AUTOCOMPLETE_QUERY_LENGTH = 3;
const AUTOCOMPLETE_DEBOUNCE_MS = 300;
const MAX_AUTOCOMPLETE_HEIGHT = 320;
const DEFAULT_AUTOCOMPLETE_Z_INDEX = 20000;
const DEFAULT_PLACE_FIELDS = [
  "addressComponents",
  "displayName",
  "formattedAddress",
  "id",
  "location",
  "plusCode",
  "types",
  "viewport",
];

const OLD_TO_NEW_FIELD_NAMES = {
  address_components: "addressComponents",
  formatted_address: "formattedAddress",
  geometry: "location",
  name: "displayName",
  place_id: "id",
  plus_code: "plusCode",
  types: "types",
};

const LEGACY_AUTOCOMPLETE_TYPE_GROUPS = [
  "address",
  "cities",
  "establishment",
  "geocode",
  "regions",
];

function toArray(value) {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
}

function getTextValue(value) {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value.text === "string") return value.text;
  if (typeof value.toString === "function") {
    const text = value.toString();
    return text === "[object Object]" ? "" : text;
  }
  return "";
}

function getPlacePrediction(suggestion) {
  return suggestion?.placePrediction || null;
}

function getPredictionText(placePrediction) {
  return getTextValue(placePrediction?.text);
}

function getPredictionMainText(placePrediction) {
  return getTextValue(placePrediction?.mainText) || getPredictionText(placePrediction);
}

function getPredictionSecondaryText(placePrediction) {
  return getTextValue(placePrediction?.secondaryText);
}

function getMapsApiKey(options) {
  return GOOGLE_MAPS_API_KEY || _.get(options, "params.key");
}

function normalizeRegion(region) {
  return String(region || "").trim().toLowerCase();
}

function mapAddressComponent(component) {
  return {
    long_name: component.long_name || component.longText || "",
    short_name: component.short_name || component.shortText || "",
    types: component.types || [],
  };
}

function mapPlusCode(plusCode) {
  if (!plusCode) return undefined;
  return {
    compound_code: plusCode.compound_code || plusCode.compoundCode,
    global_code: plusCode.global_code || plusCode.globalCode,
  };
}

function getLegacyGeometry(place) {
  const geometry = {};

  if (place.location) {
    geometry.location = place.location;
  }

  if (place.viewport) {
    geometry.viewport = place.viewport;
  }

  return Object.keys(geometry).length ? geometry : undefined;
}

function renderSuggestion(suggestion) {
  const placePrediction = getPlacePrediction(suggestion);
  const option = document.createElement("div");
  const mainText = document.createElement("div");
  const secondaryText = getPredictionSecondaryText(placePrediction);

  option.className = `${GOOGLE_ADDRESS_CLASS}__option`;
  mainText.className = `${GOOGLE_ADDRESS_CLASS}__main`;
  mainText.textContent = getPredictionMainText(placePrediction);
  option.appendChild(mainText);

  if (secondaryText) {
    const secondary = document.createElement("div");
    secondary.className = `${GOOGLE_ADDRESS_CLASS}__secondary`;
    secondary.textContent = secondaryText;
    option.appendChild(secondary);
  }

  return option;
}

function appendAttribution(container) {
  if (container.querySelector(`.${GOOGLE_ADDRESS_CLASS}__attribution`)) {
    return;
  }

  const attribution = document.createElement("div");
  attribution.className = `${GOOGLE_ADDRESS_CLASS}__attribution`;
  attribution.textContent = "Google Maps";
  container.appendChild(attribution);
}

function getElementZIndex(element) {
  if (!element) return 0;

  const zIndex = Number.parseInt(window.getComputedStyle(element).zIndex, 10);
  return Number.isFinite(zIndex) ? zIndex : 0;
}

function getAutocompleteZIndex(input) {
  const host = input.closest(".osjs-window, .k-window, .modal, .modal-container");
  return Math.max(DEFAULT_AUTOCOMPLETE_Z_INDEX, getElementZIndex(host) + 1);
}

function ensureGoogleAddressStyles() {
  if (
    typeof document === "undefined" ||
    document.getElementById(GOOGLE_ADDRESS_STYLE_ID)
  ) {
    return;
  }

  const style = document.createElement("style");
  style.id = GOOGLE_ADDRESS_STYLE_ID;
  style.textContent = `
    .autocomplete.${GOOGLE_ADDRESS_CLASS} {
      z-index: ${DEFAULT_AUTOCOMPLETE_Z_INDEX};
      overflow-y: auto;
      background: #fff;
      border: 1px solid rgba(0, 0, 0, 0.15);
      border-radius: 0.25rem;
      box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
      color: #212529;
      font-family: inherit;
      font-size: 0.875rem;
      line-height: 1.4;
    }

    .autocomplete.${GOOGLE_ADDRESS_CLASS} * {
      font: inherit;
    }

    .autocomplete.${GOOGLE_ADDRESS_CLASS} > div {
      padding: 0;
    }

    .autocomplete.${GOOGLE_ADDRESS_CLASS} > .${GOOGLE_ADDRESS_CLASS}__option {
      min-height: 44px;
      padding: 0.5rem 0.75rem;
      border-bottom: 1px solid #e9ecef;
      cursor: pointer;
      background: #fff;
    }

    .autocomplete.${GOOGLE_ADDRESS_CLASS} > .${GOOGLE_ADDRESS_CLASS}__option:hover,
    .autocomplete.${GOOGLE_ADDRESS_CLASS} > .${GOOGLE_ADDRESS_CLASS}__option.selected {
      background: #f8f9fa;
    }

    .${GOOGLE_ADDRESS_CLASS}__main {
      color: #212529;
      font-weight: 600;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .${GOOGLE_ADDRESS_CLASS}__secondary {
      margin-top: 2px;
      color: #6c757d;
      font-size: 0.8125rem;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .autocomplete.${GOOGLE_ADDRESS_CLASS} > .${GOOGLE_ADDRESS_CLASS}__attribution {
      position: sticky;
      bottom: 0;
      padding: 0.375rem 0.75rem;
      border-top: 1px solid #e9ecef;
      color: #6c757d;
      font-size: 0.75rem;
      text-align: right;
      background: #fff;
    }
  `;
  document.head.appendChild(style);
}

function removeEmptyValues(request) {
  return _.pickBy(request, (value) => {
    if (value === undefined || value === null || value === "") {
      return false;
    }
    return !(Array.isArray(value) && value.length === 0);
  });
}

export class GoogleAddressProvider extends AddressProvider {
  static get name() {
    return "google";
  }

  static get displayName() {
    return "Google Maps";
  }

  constructor(options = {}) {
    super(options);
    this.setAutocompleteOptions();
    this.loadGoogleMaps();
  }

  get displayValueProperty() {
    return "formattedPlace";
  }

  get alternativeDisplayValueProperty() {
    return "formatted_address";
  }

  get autocompleteOptions() {
    return this._autocompleteOptions;
  }

  set autocompleteOptions(options) {
    this._autocompleteOptions = options;
  }

  beforeMergeOptions(options) {
    this.convertRegionToAutocompleteOption(options);
  }

  getLibraryName() {
    return GOOGLE_MAPS_LIBRARY;
  }

  convertRegionToAutocompleteOption(options) {
    const providerOptions = options;
    let region = _.get(providerOptions, "params.region", "");

    if (region && !_.has(options, "params.autocompleteOptions")) {
      region = region.toUpperCase().trim();
      const countryCodes = {
        UK: "GB",
      };

      if (countryCodes[region]) {
        region = countryCodes[region];
      }

      _.set(
        providerOptions,
        "params.autocompleteOptions.componentRestrictions.country",
        [region]
      );
    }
  }

  getRequiredAddressProperties() {
    return [
      "address_components",
      "formatted_address",
      "geometry",
      "place_id",
      "plus_code",
      "types",
    ];
  }

  setAutocompleteOptions() {
    let options = _.get(this.options, "params.autocompleteOptions", {});
    if (!_.isObject(options)) {
      options = {};
    }

    this.addRequiredProviderOptions(options);
    this.autocompleteOptions = options;
  }

  addRequiredProviderOptions(options) {
    const addressProperties = this.getRequiredAddressProperties();
    if (_.isArray(options.fields) && options.fields.length > 0) {
      options.fields.forEach((optionalField) => {
        if (!addressProperties.some((addressProp) => optionalField === addressProp)) {
          addressProperties.push(optionalField);
        }
      });
    }
    options.fields = addressProperties;
  }

  getPlaceFields() {
    const fields = new Set(DEFAULT_PLACE_FIELDS);

    toArray(this.autocompleteOptions?.fields).forEach((field) => {
      const newField = OLD_TO_NEW_FIELD_NAMES[field] || field;
      fields.add(newField);
      if (field === "geometry") {
        fields.add("viewport");
      }
    });

    return [...fields];
  }

  getMapsScriptUrl() {
    const params = new URLSearchParams({
      v: "quarterly",
      libraries: "places",
      loading: "async",
      callback: `${GOOGLE_MAPS_LIBRARY}Callback`,
    });
    const key = getMapsApiKey(this.options);
    const region = _.get(this.options, "params.region");

    if (key) {
      params.set("key", key);
    }

    if (region) {
      params.set("region", region);
    }

    return `${GOOGLE_MAPS_BASE_URL}?${params.toString()}`;
  }

  loadGoogleMaps() {
    if (window.google?.maps?.importLibrary) {
      return Promise.resolve(window.google.maps);
    }

    return Formio.requireLibrary(
      this.getLibraryName(),
      "google.maps.importLibrary",
      this.getMapsScriptUrl()
    ).then(() => {
      if (!window.google?.maps?.importLibrary) {
        throw new Error("Google Maps importLibrary was not available after loading.");
      }

      return window.google.maps;
    });
  }

  loadPlacesLibrary() {
    return this.loadGoogleMaps().then((maps) => maps.importLibrary("places"));
  }

  async createAutocompleteSessionToken() {
    const placesLibrary = await this.loadPlacesLibrary();
    const AutocompleteSessionToken =
      placesLibrary?.AutocompleteSessionToken ||
      window.google?.maps?.places?.AutocompleteSessionToken;

    return AutocompleteSessionToken ? new AutocompleteSessionToken() : undefined;
  }

  getAutocompleteRequest(input, sessionToken) {
    const {
      bounds,
      componentRestrictions,
      locationBias,
      locationRestriction,
      origin,
      strictBounds,
      types,
    } = this.autocompleteOptions || {};
    const countryCodes = toArray(componentRestrictions?.country)
      .map(normalizeRegion)
      .filter(Boolean);
    const primaryTypes = toArray(types).filter(
      (type) => type && !LEGACY_AUTOCOMPLETE_TYPE_GROUPS.includes(type)
    );
    const request = {
      input,
      sessionToken,
      includedPrimaryTypes: primaryTypes,
      includedRegionCodes: countryCodes,
      language:
        _.get(this.options, "params.language") ||
        _.get(this.options, "params.autocompleteOptions.language"),
      region: normalizeRegion(_.get(this.options, "params.region")),
      locationBias,
      locationRestriction,
      origin,
    };

    if (bounds && !request.locationBias && !request.locationRestriction) {
      request[strictBounds ? "locationRestriction" : "locationBias"] = bounds;
    }

    return removeEmptyValues(request);
  }

  async fetchAutocompleteSuggestions(input, sessionToken) {
    const placesLibrary = await this.loadPlacesLibrary();
    const AutocompleteSuggestion =
      placesLibrary?.AutocompleteSuggestion ||
      window.google?.maps?.places?.AutocompleteSuggestion;

    if (!AutocompleteSuggestion?.fetchAutocompleteSuggestions) {
      throw new Error("AutocompleteSuggestion.fetchAutocompleteSuggestions is not available.");
    }

    const { suggestions = [] } =
      await AutocompleteSuggestion.fetchAutocompleteSuggestions(
        this.getAutocompleteRequest(input, sessionToken)
      );

    return suggestions.filter(getPlacePrediction);
  }

  toLegacyPlace(place, placePrediction) {
    const addressComponents = (place.addressComponents || place.address_components || [])
      .map(mapAddressComponent);
    const formattedAddress = place.formattedAddress || place.formatted_address || "";
    const displayName = getTextValue(place.displayName || place.name);
    const formattedPlace = getPredictionText(placePrediction) || formattedAddress || displayName;
    const geometry = getLegacyGeometry(place);
    const legacyPlace = {
      address_components: addressComponents,
      formatted_address: formattedAddress,
      formattedPlace,
      name: displayName,
      place_id: place.id || place.place_id,
      types: place.types || [],
    };
    const plusCode = mapPlusCode(place.plusCode || place.plus_code);

    if (geometry) {
      legacyPlace.geometry = geometry;
    }

    if (plusCode) {
      legacyPlace.plus_code = plusCode;
    }

    return this.filterPlace(legacyPlace);
  }

  filterPlace(place) {
    place = place || {};
    const filteredPlace = {};

    if (this.autocompleteOptions) {
      this.autocompleteOptions.fields.forEach((field) => {
        if (place[field]) {
          filteredPlace[field] = place[field];
        }
      });
    }

    if (place.formattedPlace) {
      filteredPlace.formattedPlace = place.formattedPlace;
    }

    return filteredPlace;
  }

  attachAutocomplete(elem, index, onSelectAddress) {
    ensureGoogleAddressStyles();

    let sessionToken = null;
    const getSessionToken = async () => {
      if (!sessionToken) {
        sessionToken = await this.createAutocompleteSessionToken();
      }
      return sessionToken;
    };
    const resetSessionToken = () => {
      sessionToken = null;
    };

    autocomplete({
      input: elem,
      minLength: MIN_AUTOCOMPLETE_QUERY_LENGTH,
      debounceWaitMs: AUTOCOMPLETE_DEBOUNCE_MS,
      preventSubmit: true,
      className: GOOGLE_ADDRESS_CLASS,
      fetch: async (text, update) => {
        try {
          const query = text.trim();
          const suggestions = await this.fetchAutocompleteSuggestions(
            query,
            await getSessionToken()
          );
          update(suggestions);
        } catch (error) {
          console.error("Google Places autocomplete error", error);
          update([]);
        }
      },
      render: renderSuggestion,
      customize: (input, inputRect, container, maxHeight) => {
        container.style.zIndex = String(getAutocompleteZIndex(input));
        container.style.maxHeight = `${Math.min(maxHeight, MAX_AUTOCOMPLETE_HEIGHT)}px`;
        appendAttribution(container);
      },
      onSelect: (suggestion) => {
        const placePrediction = getPlacePrediction(suggestion);
        elem.value = getPredictionText(placePrediction);
        this.selectSuggestion(
          suggestion,
          elem,
          index,
          onSelectAddress,
          resetSessionToken
        );
      },
    });
  }

  async selectSuggestion(
    suggestion,
    elem,
    index,
    onSelectAddress,
    resetSessionToken
  ) {
    const placePrediction = getPlacePrediction(suggestion);
    if (!placePrediction) {
      resetSessionToken();
      return;
    }

    try {
      const place = placePrediction.toPlace();
      await place.fetchFields({
        fields: this.getPlaceFields(),
      });

      const selectedAddress = this.toLegacyPlace(place, placePrediction);
      elem.value = this.getDisplayValue(selectedAddress);
      onSelectAddress(selectedAddress, elem, index);
    } catch (error) {
      console.error("Google Places details error", error);
    } finally {
      resetSessionToken();
    }
  }

  search(query) {
    return this.fetchAutocompleteSuggestions(query);
  }

  makeRequest() {
    return Promise.resolve([]);
  }

  getDisplayValue(address) {
    const displayedProperty = _.has(address, this.displayValueProperty)
      ? this.displayValueProperty
      : this.alternativeDisplayValueProperty;

    return _.get(address, displayedProperty, "");
  }
}

export default function registerGoogleAddressProvider(FormioInstance = Formio) {
  if (!FormioInstance?.Providers?.addProvider) {
    return;
  }

  FormioInstance.Providers.addProvider("address", GoogleAddressProvider.name, GoogleAddressProvider);
}
